import gkrFixture from "src/app/organisms/wallet-pass-preview/fixtures/preview-gkr.json";
import standardFixture from "src/app/organisms/wallet-pass-preview/fixtures/preview-standard.json";
import toomFixture from "src/app/organisms/wallet-pass-preview/fixtures/preview-toom.json";
import { DesignPreviewAnnotationDto, DesignPreviewResponseDto } from "@api/wallet-pass-service";

type PreviewPlatform = "ios" | "android";
type PreviewMode = "standard" | "toom" | "gkr";
type FieldSource =
  | "walletDesign"
  | "accountCustomProperties"
  | "accountBalance"
  | "statusLevel"
  | "passMetadata"
  | "previewDemo"
  | "program"
  | "scannableConfig"
  | "platformPolicy";

type AnnotationSurface = "preview" | "customizations" | "form";

interface AppleField {
  key: string;
  label: string;
  value: string | number;
}

interface GoogleManifest {
  omitPrimaryLoyaltyPoints: boolean;
  issuerName?: string;
  programName?: string;
  loyaltyPointsLabel?: string;
  loyaltyPointsBalance?: string;
  secondaryLoyaltyPointsLabel?: string;
  secondaryLoyaltyPointsBalance?: string;
  textModules: { id?: string; header?: string; body?: string }[];
  barcodeAlternateText?: string;
}

const ZWSP = "\u200b";

const SOURCE_LABELS: Record<string, Record<FieldSource, string>> = {
  en: {
    walletDesign: "Wallet design",
    accountCustomProperties: "Account custom properties",
    accountBalance: "Account balance",
    statusLevel: "Status level",
    passMetadata: "Pass metadata",
    previewDemo: "Preview demo",
    program: "Program",
    scannableConfig: "Scannable code",
    platformPolicy: "Platform policy",
  },
  es: {
    walletDesign: "Diseño wallet",
    accountCustomProperties: "Custom properties de la cuenta",
    accountBalance: "Saldo de la cuenta",
    statusLevel: "Nivel de estado",
    passMetadata: "Metadatos del pase",
    previewDemo: "Demo de preview",
    program: "Programa",
    scannableConfig: "Código escaneable",
    platformPolicy: "Política de plataforma",
  },
  de: {
    walletDesign: "Wallet-Design",
    accountCustomProperties: "Account-Custom-Properties",
    accountBalance: "Kontostand",
    statusLevel: "Statusstufe",
    passMetadata: "Pass-Metadaten",
    previewDemo: "Preview-Demo",
    program: "Programm",
    scannableConfig: "Scanbarer Code",
    platformPolicy: "Plattformrichtlinie",
  },
};

const COPY: Record<
  string,
  {
    balanceNameLabel: { label: string; tooltip: string };
    balanceValue: { label: string; tooltip: string };
    tier: { label: string; tooltip: string };
    backgroundColor: { label: string; tooltip: string };
    foregroundColor: { label: string; tooltip: string };
    icon: { label: string; tooltip: string };
    passLogo: { label: string; tooltip: string };
    bannerImage: { label: string; tooltip: string };
    barcode: { label: string; tooltip: string };
    issuerStandard: { label: string; tooltip: string };
    issuerToom: { label: string; tooltip: string };
    programGkr: { label: string; tooltip: string };
    toomFace: string;
    notify: { label: string; valueOn: string; tooltip: string };
  }
> = {
  en: {
    balanceNameLabel: {
      label: "Balance label",
      tooltip: "Label shown next to the points balance.\nSource: {source}.",
    },
    balanceValue: {
      label: "Points balance",
      tooltip: "Points value on the pass.\nSource: {source}.",
    },
    tier: {
      label: "Tier",
      tooltip: "Membership tier on the pass.\nSource: {source}.",
    },
    backgroundColor: {
      label: "Background color",
      tooltip: "Pass background color.\nSource: {source}.",
    },
    foregroundColor: {
      label: "Text color",
      tooltip: "Apple Wallet text/label color only. Google Wallet picks text color automatically for contrast.\nSource: {source}.",
    },
    icon: { label: "Icon", tooltip: "Program icon.\nSource: {source}." },
    passLogo: {
      label: "Pass logo",
      tooltip: "Logo on the pass (Apple prefers pass logo; Google may use icon).\nSource: {source}.",
    },
    bannerImage: {
      label: "Banner image",
      tooltip: "Banner / strip image.\nSource: {source}.",
    },
    barcode: {
      label: "Scannable code",
      tooltip: "Scannable code type (QR or Aztec) and content on the pass.\nSource: {source}.",
    },
    issuerStandard: {
      label: "Program name (card title)",
      tooltip: "Shown as the Google Wallet card title (Android). Not used as the Apple Wallet title.\nSource: {source}.",
    },
    issuerToom: {
      label: "Issuer title",
      tooltip: "Toom issuer title (Vorteilskarte). Fixed by the program integration.\nSource: {source}.",
    },
    programGkr: {
      label: "Program headline",
      tooltip: "Large Google Wallet headline above the points row (Android, GKR layout). Not shown on Apple Wallet.\nSource: {source}.",
    },
    toomFace: "Program integration field on the pass.\nSource: {source}.",
    notify: {
      label: "Update on custom property change",
      valueOn: "On",
      tooltip:
        "Program-specific pass update when account custom properties change. Configured via API/ops only (not editable in Design).\nSource: {source}.",
    },
  },
  es: {
    balanceNameLabel: {
      label: "Etiqueta de saldo",
      tooltip: "Etiqueta junto al saldo de puntos.\nOrigen: {source}.",
    },
    balanceValue: {
      label: "Saldo de puntos",
      tooltip: "Valor de puntos en el pase.\nOrigen: {source}.",
    },
    tier: {
      label: "Nivel",
      tooltip: "Nivel de membresía en el pase.\nOrigen: {source}.",
    },
    backgroundColor: {
      label: "Color de fondo",
      tooltip: "Color de fondo del pase.\nOrigen: {source}.",
    },
    foregroundColor: {
      label: "Color de texto",
      tooltip:
        "Solo color de texto/etiquetas de Apple Wallet. Google Wallet elige el color automáticamente para contraste.\nOrigen: {source}.",
    },
    icon: { label: "Icono", tooltip: "Icono del programa.\nOrigen: {source}." },
    passLogo: {
      label: "Logo del pase",
      tooltip: "Logo del pase (Apple prefiere pass logo; Google puede usar el icono).\nOrigen: {source}.",
    },
    bannerImage: {
      label: "Imagen de banner",
      tooltip: "Banner / strip del pase.\nOrigen: {source}.",
    },
    barcode: {
      label: "Código escaneable",
      tooltip: "Tipo de código escaneable (QR o Aztec) y contenido en el pase.\nOrigen: {source}.",
    },
    issuerStandard: {
      label: "Nombre del programa (título)",
      tooltip: "Se muestra como título de la tarjeta en Google Wallet (Android). No se usa como título en Apple Wallet.\nOrigen: {source}.",
    },
    issuerToom: {
      label: "Título del emisor",
      tooltip: "Título Toom (Vorteilskarte). Fijo por la integración del programa.\nOrigen: {source}.",
    },
    programGkr: {
      label: "Titular del programa",
      tooltip:
        "Titular grande encima de los puntos en Google Wallet (Android, layout GKR). No se muestra en Apple Wallet.\nOrigen: {source}.",
    },
    toomFace: "Campo de la integración del programa en el pase.\nOrigen: {source}.",
    notify: {
      label: "Actualizar al cambiar propiedades personalizadas",
      valueOn: "Activado",
      tooltip: "Actualización del pase específica del programa al cambiar custom properties. Solo vía API/ops.\nOrigen: {source}.",
    },
  },
  de: {
    balanceNameLabel: {
      label: "Guthabenbezeichnung",
      tooltip: "Bezeichnung neben dem Punkteguthaben.\nQuelle: {source}.",
    },
    balanceValue: {
      label: "Punkteguthaben",
      tooltip: "Punktewert auf dem Pass.\nQuelle: {source}.",
    },
    tier: {
      label: "Stufe",
      tooltip: "Mitgliedsstufe auf dem Pass.\nQuelle: {source}.",
    },
    backgroundColor: {
      label: "Hintergrundfarbe",
      tooltip: "Pass-Hintergrundfarbe.\nQuelle: {source}.",
    },
    foregroundColor: {
      label: "Textfarbe",
      tooltip: "Nur Apple-Wallet-Textfarbe. Google Wallet wählt die Textfarbe automatisch für Kontrast.\nQuelle: {source}.",
    },
    icon: { label: "Icon", tooltip: "Programm-Icon.\nQuelle: {source}." },
    passLogo: {
      label: "Pass-Logo",
      tooltip: "Logo auf dem Pass (Apple bevorzugt Pass-Logo; Google kann Icon nutzen).\nQuelle: {source}.",
    },
    bannerImage: {
      label: "Bannerbild",
      tooltip: "Banner / Strip auf dem Pass.\nQuelle: {source}.",
    },
    barcode: {
      label: "Scanbarer Code",
      tooltip: "Typ des scanbaren Codes (QR oder Aztec) und Inhalt auf dem Pass.\nQuelle: {source}.",
    },
    issuerStandard: {
      label: "Programmname (Kartentitel)",
      tooltip: "Als Google-Wallet-Kartentitel (Android). Wird nicht als Apple-Wallet-Titel verwendet.\nQuelle: {source}.",
    },
    issuerToom: {
      label: "Ausstellertitel",
      tooltip: "Toom-Ausstellertitel (Vorteilskarte). Durch die Programmintegration festgelegt.\nQuelle: {source}.",
    },
    programGkr: {
      label: "Programm-Überschrift",
      tooltip: "Große Google-Wallet-Überschrift über der Punktezeile (Android, GKR-Layout). Nicht auf Apple Wallet.\nQuelle: {source}.",
    },
    toomFace: "Feld der Programmintegration auf dem Pass.\nQuelle: {source}.",
    notify: {
      label: "Update bei Änderung benutzerdefinierter Eigenschaften",
      valueOn: "An",
      tooltip: "Programmspezifisches Pass-Update bei Änderung von Custom Properties. Nur über API/Ops.\nQuelle: {source}.",
    },
  },
};

const SUMMARIES: Record<PreviewMode, Record<string, { title: string; description: string }>> = {
  standard: {
    en: {
      title: "Standard loyalty layout",
      description:
        "Apple shows balance (using the balance label) and tier. Google shows points and optional tier. Design controls colors, images, barcode, and update notifications.",
    },
    es: {
      title: "Diseño loyalty estándar",
      description:
        "Apple muestra saldo (con la etiqueta de saldo) y nivel. Google muestra puntos y nivel opcional. Design controla colores, imágenes, código y notificaciones de actualización.",
    },
  },
  toom: {
    en: {
      title: "Toom Vorteilskarte layout",
      description:
        "Program integration layout: optional Bonus/Gutschein/Rabatt in the header; Name, Aktualisiert, and Nr below (Apple) or as text modules (Google). Issuer shows as Vorteilskarte; no points balance; Aztec without a visible caption; Apple sharing disabled. Design mainly controls colors, logos, banner, and the Aztec template—not member fields. Balance label and balance notifications do not affect the card layout.",
    },
    es: {
      title: "Diseño Toom Vorteilskarte",
      description:
        "Layout de la integración del programa: Bonus/Gutschein/Rabatt opcional en cabecera; Name, Aktualisiert y Nr debajo (Apple) o como text modules (Google). Emisor Vorteilskarte; sin saldo de puntos; Aztec sin caption visible; compartir desactivado en Apple. Design controla sobre todo colores, logos, banner y la plantilla Aztec, no los campos de socio. La etiqueta de saldo y las notificaciones de saldo no afectan el layout.",
    },
  },
  gkr: {
    en: {
      title: "GKR Beer Thaler layout",
      description:
        "Formatted Beer Thaler balance (points ÷ 100) is the primary face field; the balance label from Design is shown on the pass. Optional Tier. Barcode caption is hidden. Google may send a balance-update push for the program.",
    },
    es: {
      title: "Diseño GKR Beer Thaler",
      description:
        "El saldo Beer Thaler formateado (puntos ÷ 100) es el campo principal; la etiqueta de saldo de Design aparece en el pase. Nivel opcional. Caption del código oculto. Google puede enviar un push de actualización de saldo del programa.",
    },
  },
};

function localeOf(acceptLanguage: string): string {
  const short = acceptLanguage.trim().slice(0, 2).toLowerCase();
  return short === "es" || short === "de" ? short : "en";
}

function pickSummary(mode: PreviewMode, locale: string): { title: string; description: string } {
  return SUMMARIES[mode][locale] ?? SUMMARIES[mode]["en"];
}

function sourceLabel(source: FieldSource, locale: string): string {
  return (SOURCE_LABELS[locale] ?? SOURCE_LABELS["en"])[source] ?? source;
}

function copyOf(locale: string): (typeof COPY)[string] {
  return COPY[locale] ?? COPY["en"];
}

function ann(params: {
  id: string;
  surfaces: AnnotationSurface[];
  target: DesignPreviewAnnotationDto["target"];
  label: string;
  value?: string;
  source: FieldSource;
  tooltip: string;
  formControl?: string | null;
  platformScope?: "ios" | "android" | "both";
  locale: string;
}): DesignPreviewAnnotationDto {
  const sourceLbl = sourceLabel(params.source, params.locale);
  return {
    id: params.id,
    surfaces: params.surfaces,
    target: params.target,
    label: params.label,
    value: params.value,
    source: params.source,
    sourceLabel: sourceLbl,
    tooltip: params.tooltip.replace("{source}", sourceLbl),
    // OpenAPI generator types nullable scalars as `object | null`; runtime JSON is a string.
    formControl: (params.formControl ?? null) as DesignPreviewAnnotationDto["formControl"],
    platformScope: params.platformScope ?? "both",
  };
}

function chromeAppearance(locale: string, platform: PreviewPlatform): DesignPreviewAnnotationDto[] {
  const c = copyOf(locale);
  return [
    ann({
      id: "chrome.backgroundColor",
      surfaces: ["preview", "form"],
      target: { kind: "chrome", slot: "backgroundColor" },
      label: c.backgroundColor.label,
      source: "walletDesign",
      tooltip: c.backgroundColor.tooltip,
      formControl: "backgroundColor",
      locale,
    }),
    ann({
      id: "chrome.icon",
      surfaces: ["preview", "form"],
      target: { kind: "chrome", slot: "icon" },
      label: c.icon.label,
      source: "walletDesign",
      tooltip: c.icon.tooltip,
      formControl: "icon",
      locale,
    }),
    ann({
      id: "chrome.passLogo",
      surfaces: ["preview", "form"],
      target: { kind: "chrome", slot: "logo" },
      label: c.passLogo.label,
      source: "walletDesign",
      tooltip: c.passLogo.tooltip,
      formControl: "passLogo",
      locale,
    }),
    ann({
      id: "chrome.bannerImage",
      surfaces: ["preview", "form"],
      target: { kind: "chrome", slot: "bannerImage" },
      label: c.bannerImage.label,
      source: "walletDesign",
      tooltip: c.bannerImage.tooltip,
      formControl: "bannerImage",
      locale,
    }),
    ann({
      id: "chrome.barcode",
      surfaces: ["preview", "form"],
      target: { kind: "chrome", slot: "barcode" },
      label: c.barcode.label,
      source: "walletDesign",
      tooltip: c.barcode.tooltip,
      formControl: "scannableType",
      locale,
    }),
    ann({
      id: "chrome.foregroundColor",
      surfaces: platform === "ios" ? ["preview", "form"] : ["form"],
      target: { kind: "chrome", slot: "foregroundColor" },
      label: c.foregroundColor.label,
      source: "walletDesign",
      tooltip: c.foregroundColor.tooltip,
      formControl: "foregroundColor",
      platformScope: "ios",
      locale,
    }),
  ];
}

function visibleGoogleText(value?: string | null): string | null {
  if (value == null) return null;
  const trimmed = value.replace(/\u200b/gi, "").trim();
  return trimmed.length > 0 ? trimmed : null;
}

function programNameFormAnnotations(params: {
  locale: string;
  platform: PreviewPlatform;
  mode: PreviewMode;
  programId: string;
  google: GoogleManifest;
}): DesignPreviewAnnotationDto[] {
  const { locale, platform, mode, programId, google } = params;
  if (mode === "toom") return [];
  const c = copyOf(locale);
  const formSurfaces: AnnotationSurface[] = platform === "android" ? ["preview", "form"] : ["form"];
  const designName = programId;

  if (mode === "gkr") {
    const largeTitle = visibleGoogleText(google.programName) ?? designName;
    const rows: DesignPreviewAnnotationDto[] = [
      ann({
        id: "chrome.programName",
        surfaces: formSurfaces,
        target: { kind: "chrome", slot: "programName" },
        label: c.programGkr.label,
        value: largeTitle,
        source: "walletDesign",
        tooltip: c.programGkr.tooltip,
        formControl: "name",
        platformScope: "android",
        locale,
      }),
    ];
    if (platform === "android") {
      rows.unshift(
        ann({
          id: "chrome.issuerName",
          surfaces: ["preview"],
          target: { kind: "chrome", slot: "issuerName" },
          label: c.issuerStandard.label,
          value: visibleGoogleText(google.issuerName) ?? designName,
          source: "walletDesign",
          tooltip: c.issuerStandard.tooltip,
          platformScope: "android",
          locale,
        }),
      );
    }
    return rows;
  }

  return [
    ann({
      id: "chrome.issuerName",
      surfaces: formSurfaces,
      target: { kind: "chrome", slot: "issuerName" },
      label: c.issuerStandard.label,
      value: visibleGoogleText(google.issuerName) ?? designName,
      source: "walletDesign",
      tooltip: c.issuerStandard.tooltip,
      formControl: "name",
      platformScope: "android",
      locale,
    }),
  ];
}

function pointsLabelAndValue(params: {
  locale: string;
  platform: PreviewPlatform;
  balanceLabel: string;
  balanceValue: string;
  region: string;
}): DesignPreviewAnnotationDto[] {
  const c = copyOf(params.locale);
  return [
    ann({
      id: "chrome.balanceNameLabel",
      surfaces: ["preview", "form"],
      target: { kind: "chrome", slot: "balanceNameLabel" },
      label: c.balanceNameLabel.label,
      value: params.balanceLabel,
      source: "walletDesign",
      tooltip: c.balanceNameLabel.tooltip,
      formControl: "balanceName",
      platformScope: params.platform,
      locale: params.locale,
    }),
    ann({
      id: `face.${params.region}.balance`,
      surfaces: ["preview"],
      target: { kind: "face", region: params.region, key: "balance" },
      label: c.balanceValue.label,
      value: params.balanceValue,
      source: "accountBalance",
      tooltip: c.balanceValue.tooltip,
      platformScope: params.platform,
      locale: params.locale,
    }),
  ];
}

function tierAnnotation(params: { locale: string; platform: PreviewPlatform; region: string; value: string }): DesignPreviewAnnotationDto {
  const c = copyOf(params.locale);
  return ann({
    id: `face.${params.region}.tier`,
    surfaces: ["preview"],
    target: { kind: "face", region: params.region, key: "tier" },
    label: c.tier.label,
    value: params.value,
    source: "statusLevel",
    tooltip: c.tier.tooltip,
    platformScope: params.platform,
    locale: params.locale,
  });
}

function resolveToomFaceSource(key: string): FieldSource {
  const normalized = key === "holder" ? "name" : key === "updated" ? "aktualisiert" : key;
  if (normalized === "aktualisiert") return "passMetadata";
  if (normalized === "name" || normalized === "bonus" || normalized === "nr") {
    return "accountCustomProperties";
  }
  return "previewDemo";
}

function toomFaceField(params: {
  locale: string;
  platform: PreviewPlatform;
  region: string;
  key: string;
  label: string;
  value: string;
}): DesignPreviewAnnotationDto {
  const source = resolveToomFaceSource(params.key);
  return ann({
    id: `face.${params.region}.${params.key}`,
    surfaces: ["preview", "customizations"],
    target: { kind: "face", region: params.region, key: params.key },
    label: params.label,
    value: params.value,
    source,
    tooltip: copyOf(params.locale).toomFace,
    platformScope: params.platform,
    locale: params.locale,
  });
}

function notifyUpdateFormAnnotations(locale: string): DesignPreviewAnnotationDto[] {
  const fields: { formControl: string; label: string; tooltip: string }[] = [
    {
      formControl: "notifyTier",
      label: "Update on tier change",
      tooltip: "Silent pass refresh when the member tier changes. Not a lock-screen alert.\nSource: {source}.",
    },
    {
      formControl: "notifyTierPush",
      label: "Notify on lock screen when tier changes",
      tooltip:
        "Visible Wallet / lock-screen alert when the member tier changes. Requires silent tier updates to stay on.\nSource: {source}.",
    },
    {
      formControl: "notifyBalance",
      label: "Update on balance change",
      tooltip: "Silent pass refresh when the points balance changes. Not a lock-screen alert.\nSource: {source}.",
    },
    {
      formControl: "notifyBalancePush",
      label: "Notify on lock screen when balance changes",
      tooltip:
        "Visible Wallet / lock-screen alert when the points balance changes. Requires silent balance updates to stay on.\nSource: {source}.",
    },
  ];
  return fields.map(field =>
    ann({
      id: `form.${field.formControl}`,
      surfaces: ["form"],
      target: { kind: "behavior", slot: field.formControl },
      label: field.label,
      source: "walletDesign",
      tooltip: field.tooltip,
      formControl: field.formControl,
      locale,
    }),
  );
}

function notifyCustomPropertiesAnnotation(mode: PreviewMode, locale: string): DesignPreviewAnnotationDto | null {
  if (mode !== "toom") return null;
  const c = copyOf(locale).notify;
  return ann({
    id: "behavior.notifyCustomProperties",
    surfaces: ["form", "customizations"],
    target: { kind: "behavior", slot: "notifyCustomProperties" },
    label: c.label,
    value: c.valueOn,
    source: "walletDesign",
    tooltip: c.tooltip,
    formControl: "notifyCustomProperties",
    locale,
  });
}

function formFieldAvailabilityFor(mode: PreviewMode, locale: string): DesignPreviewResponseDto["formFieldAvailability"] {
  if (mode !== "toom") {
    return {
      balanceName: { enabled: true, reason: null },
      notifyBalance: { enabled: true, reason: null },
      notifyTier: { enabled: true, reason: null },
      notifyTierPush: { enabled: true, reason: null },
      notifyBalancePush: { enabled: true, reason: null },
      scannableType: { enabled: true, reason: null },
      contentTemplate: { enabled: true, reason: null },
    };
  }

  const reasons =
    locale === "es"
      ? {
          balanceName:
            "Este programa muestra un porcentaje de bonus en lugar de un saldo de puntos. El nombre del saldo no se usa en el pase.",
          notifyBalance:
            "Este programa no muestra saldo de puntos en el pase. Las notificaciones de saldo no cambian el diseño de la tarjeta.",
          notifyTier:
            "Este programa muestra el estado del socio desde custom properties de la cuenta (vtkstatus), no el nivel de loyalty de producto. Las notificaciones de cambio de nivel no cambian el diseño de la tarjeta.",
          notifyCustomProperties:
            "Las actualizaciones del pase al cambiar custom properties se configuran solo vía API/ops (no editables en Design).",
          scannableType: "Los pases Toom en producción usan Aztec. Cambiar el tipo puede romper el escaneo en tienda.",
          contentTemplate:
            "El payload Aztec lo define la integración del programa (VTK / App Promo). Cambiar la plantilla puede romper el escaneo en tienda.",
          notifyVisiblePush:
            "Este diseño no usa alertas visibles de producto. Las actualizaciones del pase siguen en segundo plano (propiedades personalizadas / actualización silenciosa).",
        }
      : {
          balanceName: "This program shows a bonus percentage instead of a points balance. The balance name is not used on the pass face.",
          notifyBalance:
            "This program does not show a points balance on the pass face. Balance notifications do not change the card layout.",
          notifyTier:
            "This program shows member status from account custom properties (vtkstatus), not the product loyalty tier. Tier-change notifications do not change the card layout.",
          notifyCustomProperties: "Pass updates on custom property change are configured via API/ops only (not editable in Design).",
          scannableType: "Production Toom passes use Aztec. Changing the type can break in-store scanning.",
          contentTemplate:
            "The Aztec payload is defined by the program integration (VTK / App Promo). Changing the template can break in-store scanning.",
          notifyVisiblePush:
            "This layout does not use product lock-screen alerts. Wallet updates stay silent (custom properties / background refresh).",
        };

  return {
    balanceName: { enabled: false, reason: reasons.balanceName as unknown as object },
    notifyBalance: { enabled: false, reason: reasons.notifyBalance as unknown as object },
    notifyTier: { enabled: false, reason: reasons.notifyTier as unknown as object },
    notifyCustomProperties: { enabled: false, reason: reasons.notifyCustomProperties as unknown as object },
    notifyTierPush: { enabled: false, reason: reasons.notifyVisiblePush as unknown as object },
    notifyBalancePush: { enabled: false, reason: reasons.notifyVisiblePush as unknown as object },
    scannableType: { enabled: true, reason: reasons.scannableType as unknown as object },
    contentTemplate: { enabled: true, reason: reasons.contentTemplate as unknown as object },
  };
}

function resolveMode(programId: string): PreviewMode {
  const id = programId.toLowerCase();
  if (id.includes("toom")) return "toom";
  if (id.includes("gkr")) return "gkr";
  return "standard";
}

function appleFields(base: Record<string, unknown>): {
  headerFields: AppleField[];
  secondaryFields: AppleField[];
  auxiliaryFields: AppleField[];
} {
  const apple = base["apple"] as
    | {
        headerFields?: AppleField[];
        secondaryFields?: AppleField[];
        auxiliaryFields?: AppleField[];
      }
    | undefined;
  return {
    headerFields: apple?.headerFields ?? [],
    secondaryFields: apple?.secondaryFields ?? [],
    auxiliaryFields: apple?.auxiliaryFields ?? [],
  };
}

function googleManifestFor(mode: PreviewMode, base: Record<string, unknown>, programId: string): GoogleManifest {
  const appearance = (base["appearance"] ?? {}) as { balanceName?: string };
  const mockPayload = (base["mockPayload"] ?? {}) as {
    name?: string;
    balance?: number;
    balanceDisplay?: string;
    tierStatus?: string;
    vtkNumber?: string;
    bonusDisplay?: string;
    bonusLabel?: string;
  };
  const levelKey = typeof base["levelKey"] === "string" ? base["levelKey"] : undefined;

  if (mode === "toom") {
    const textModules: GoogleManifest["textModules"] = [
      { id: "holder", header: "Name", body: mockPayload.name ?? "Anna Schmidt" },
      { id: "nr", header: "Nr", body: mockPayload.vtkNumber ?? "VTKDEMO001" },
    ];
    if (mockPayload.bonusDisplay && mockPayload.bonusLabel) {
      textModules.push({
        id: "bonus",
        header: mockPayload.bonusLabel,
        body: mockPayload.bonusDisplay,
      });
    }
    textModules.push({ id: "updated", header: "Aktualisiert", body: "07.08.2026" });
    return {
      omitPrimaryLoyaltyPoints: true,
      issuerName: "Vorteilskarte",
      programName: ZWSP,
      textModules,
      barcodeAlternateText: ZWSP,
    };
  }

  if (mode === "gkr") {
    const balanceLabel = appearance.balanceName ?? "Beer Thaler";
    const balanceValue = mockPayload.balanceDisplay ?? String(mockPayload.balance ?? "0");
    return {
      omitPrimaryLoyaltyPoints: false,
      // Small title is painted from Design programDisplayName; large headline too (FE prefers form name).
      issuerName: ZWSP,
      programName: ZWSP,
      loyaltyPointsLabel: balanceLabel,
      loyaltyPointsBalance: balanceValue,
      ...(levelKey
        ? {
            secondaryLoyaltyPointsLabel: "Tier",
            secondaryLoyaltyPointsBalance: levelKey,
          }
        : {}),
      textModules: [],
      barcodeAlternateText: ZWSP,
    };
  }

  const balanceLabel = appearance.balanceName ?? "Points";
  return {
    omitPrimaryLoyaltyPoints: false,
    issuerName: programId,
    programName: ZWSP,
    loyaltyPointsLabel: balanceLabel,
    loyaltyPointsBalance: String(mockPayload.balance ?? 100),
    secondaryLoyaltyPointsLabel: "Tier",
    secondaryLoyaltyPointsBalance: mockPayload.tierStatus ?? levelKey ?? "Gold",
    textModules: [],
    barcodeAlternateText: String((base["scannableCode"] as { previewMessage?: string } | undefined)?.previewMessage ?? "preview"),
  };
}

function buildAnnotations(params: {
  mode: PreviewMode;
  platform: PreviewPlatform;
  locale: string;
  programId: string;
  base: Record<string, unknown>;
  google: GoogleManifest;
}): DesignPreviewAnnotationDto[] {
  const { mode, platform, locale, programId, base, google } = params;
  const appearance = (base["appearance"] ?? {}) as { balanceName?: string };
  const balanceName = appearance.balanceName ?? "Points";
  const rows: DesignPreviewAnnotationDto[] = [...chromeAppearance(locale, platform)];
  const c = copyOf(locale);

  if (platform === "ios") {
    const apple = appleFields(base);
    if (mode === "toom") {
      for (const field of apple.headerFields) {
        rows.push(
          toomFaceField({
            locale,
            platform,
            region: "header",
            key: field.key,
            label: field.label,
            value: String(field.value),
          }),
        );
      }
      for (const field of apple.secondaryFields) {
        rows.push(
          toomFaceField({
            locale,
            platform,
            region: "secondary",
            key: field.key,
            label: field.label,
            value: String(field.value),
          }),
        );
      }
      for (const field of apple.auxiliaryFields) {
        rows.push(
          toomFaceField({
            locale,
            platform,
            region: "auxiliary",
            key: field.key,
            label: field.label,
            value: String(field.value),
          }),
        );
      }
      rows.push(
        ann({
          id: "chrome.issuerName",
          surfaces: ["preview", "customizations"],
          target: { kind: "chrome", slot: "issuerName" },
          label: c.issuerToom.label,
          value: "Vorteilskarte",
          source: "program",
          tooltip: c.issuerToom.tooltip,
          platformScope: "ios",
          locale,
        }),
      );
    } else {
      const balanceField = apple.headerFields.find(f => f.key === "balance");
      if (balanceField) {
        rows.push(
          ...pointsLabelAndValue({
            locale,
            platform,
            balanceLabel: String(balanceField.label || balanceName),
            balanceValue: String(balanceField.value),
            region: "header",
          }),
        );
      }
      const tierField = apple.secondaryFields.find(f => f.key === "tier");
      if (tierField) {
        rows.push(
          tierAnnotation({
            locale,
            platform,
            region: "secondary",
            value: String(tierField.value),
          }),
        );
      }
    }
  }

  if (platform === "android") {
    if (mode === "toom") {
      for (const module of google.textModules) {
        const key = module.id ?? "text";
        rows.push(
          toomFaceField({
            locale,
            platform,
            region: "textModule",
            key,
            label: module.header ?? "",
            value: module.body ?? "",
          }),
        );
      }
      rows.push(
        ann({
          id: "chrome.issuerName",
          surfaces: ["preview", "customizations"],
          target: { kind: "chrome", slot: "issuerName" },
          label: c.issuerToom.label,
          value: google.issuerName ?? "Vorteilskarte",
          source: "program",
          tooltip: c.issuerToom.tooltip,
          platformScope: "android",
          locale,
        }),
      );
    } else {
      if (!google.omitPrimaryLoyaltyPoints && google.loyaltyPointsBalance !== undefined) {
        rows.push(
          ...pointsLabelAndValue({
            locale,
            platform,
            balanceLabel: google.loyaltyPointsLabel ?? balanceName,
            balanceValue: google.loyaltyPointsBalance,
            region: "points",
          }),
        );
      }
      if (google.secondaryLoyaltyPointsBalance !== undefined) {
        rows.push(
          tierAnnotation({
            locale,
            platform,
            region: "points",
            value: google.secondaryLoyaltyPointsBalance,
          }),
        );
      }
    }
  }

  rows.push(
    ...programNameFormAnnotations({
      locale,
      platform,
      mode,
      programId,
      google,
    }),
  );
  rows.push(...notifyUpdateFormAnnotations(locale));

  const notify = notifyCustomPropertiesAnnotation(mode, locale);
  if (notify) rows.push(notify);

  return rows;
}

/** Build a DesignPreviewResponseDto fixture for local FE mock / unit tests. */
export function buildWalletDesignPreviewMock(params: {
  programId: string;
  platform: PreviewPlatform;
  acceptLanguage?: string;
}): DesignPreviewResponseDto {
  const locale = localeOf(params.acceptLanguage ?? "en");
  const mode = resolveMode(params.programId);
  const base =
    mode === "toom"
      ? (structuredClone(toomFixture) as Record<string, unknown>)
      : mode === "gkr"
        ? (structuredClone(gkrFixture) as Record<string, unknown>)
        : (structuredClone(standardFixture) as Record<string, unknown>);

  const google = googleManifestFor(mode, base, params.programId);
  const annotations = buildAnnotations({
    mode,
    platform: params.platform,
    locale,
    programId: params.programId,
    base,
    google,
  });

  const response = {
    ...(base as unknown as DesignPreviewResponseDto),
    convercusProgramId: params.programId,
    platform: params.platform,
    annotations,
    integrationSummary: pickSummary(mode, locale),
    formFieldAvailability: formFieldAvailabilityFor(mode, locale),
  };

  if (params.platform === "android") {
    response.google = google as unknown as DesignPreviewResponseDto["google"];
  }

  return response;
}
